import { SafeError } from '@benzinga/safe-await';
import { SubscribableEvent } from '../subscribable';
import { SocketV2State } from './stateMachine';
import { ReconnectInfo, RetryConfig } from './retryPolicy';
import { SocketData } from './sendQueue';
import { HeartbeatConfig } from './heartbeat';
import { NetworkConfig } from './networkMonitor';

export type UrlProvider = string | URL | (() => string | URL | Promise<string | URL>);

export type SocketLogger = (entry: {
  level: 'debug' | 'info' | 'warn' | 'error';
  event: string;
  state: SocketV2State;
  meta?: Record<string, unknown>;
}) => void;

export interface SocketStats {
  connectCount: number;
  dropCount: number;
  totalDowntimeMs: number;
  currentState: SocketV2State;
  queueDepth: number;
  lastError?: string;
}

/**
 * Default budget for a single connection attempt: URL resolution + WebSocket handshake.
 * Roughly v1's open()-armed watchdog (~35-40s) rounded down.
 */
export const DEFAULT_CONNECT_TIMEOUT_MS = 30_000;

export interface SocketV2Config<RESP = unknown, REQ = unknown> {
  retry?: Partial<RetryConfig>;
  /**
   * Per-attempt connect budget in ms, covering URL resolution AND the WebSocket handshake.
   * When it elapses the attempt is aborted and routed through the normal drop/backoff path.
   * Defaults to DEFAULT_CONNECT_TIMEOUT_MS; 0 disables the watchdog entirely.
   */
  connectTimeoutMs?: number;
  heartbeat?: HeartbeatConfig<RESP, REQ>;
  sendQueue?: { maxSize?: number };
  network?: Partial<NetworkConfig>;
  /** Close codes that mean "do not retry" — transition to 'failed'. Default: 1008 and 4000–4099. */
  fatalCloseCodes?: number[];
  /** Extract a server-mandated retry delay from a close event; overrides local backoff when present. */
  retryAfterFromClose?: (event: CloseEvent) => number | undefined;
  binaryType?: BinaryType;
  logger?: SocketLogger;
}

export type SubscribableSocketV2Event<RESP> =
  | SubscribableEvent<'open'>
  | SubscribableEvent<'closing'>
  | { type: 'close'; event: CloseEvent }
  | { type: 'error'; errorEvent: SafeError }
  | { type: 'request'; msg: SocketData }
  | { type: 'response'; msg: RESP }
  | { type: 'disconnected'; errorEvent?: CloseEvent; info: ReconnectInfo }
  | { type: 'reconnecting'; errorEvent?: CloseEvent; info: ReconnectInfo }
  | { type: 'reconnected'; info: ReconnectInfo }
  | { type: 'suspended'; reason: 'offline' | 'cooldown' }
  | SubscribableEvent<'resumed'>
  | { type: 'failed'; reason: 'max-retries' | 'fatal-close-code'; event?: CloseEvent }
  | SubscribableEvent<'queue-overflow'>;

export const isFatalCloseCodeDefault = (code: number): boolean => code === 1008 || (code >= 4000 && code <= 4099);
